import React, { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { whatsappLink, LOGO_IMG } from "../../lib/site";
import { SPECIALTIES, specialtyHref } from "../../lib/specialties";

const links = [
  { id: "hero", label: "Home", href: "/" },
  { id: "sobre", label: "Sobre", href: "/#sobre" },
  { id: "especialidades", label: "Especialidades", dropdown: true },
  { id: "faq", label: "FAQ", href: "/#faq" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [hoverDrop, setHoverDrop] = useState(false);
  const [mobileSpecOpen, setMobileSpecOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-[#E9ECEF]/70"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between gap-4">
        {/* Brand */}
        <a href="/" data-testid="brand-link" className="flex items-center gap-3 group flex-shrink-0">
          <img
            src={LOGO_IMG}
            alt="Dra. Luz Adriana — logo"
            className="w-11 h-11 rounded-full object-cover ring-1 ring-[#D4AF37]/40"
          />
          <span className="font-serif-display text-lg text-[#0A2540] leading-none hidden sm:inline">
            Dra. Luz Adriana
          </span>
        </a>

        {/* Centered nav */}
        <nav className="hidden lg:flex items-center gap-10 absolute left-1/2 -translate-x-1/2">
          {links.map((l) =>
            l.dropdown ? (
              <div
                key={l.id}
                className="relative"
                onMouseEnter={() => setHoverDrop(true)}
                onMouseLeave={() => setHoverDrop(false)}
              >
                <button
                  data-testid={`nav-${l.id}`}
                  className="flex items-center gap-1 text-sm text-[#0A2540]/85 hover:text-[#D4AF37] transition-colors"
                  aria-haspopup="true"
                  aria-expanded={hoverDrop}
                >
                  {l.label}
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${hoverDrop ? "rotate-180" : ""}`}
                  />
                </button>
                {hoverDrop && (
                  <div
                    data-testid="specialties-dropdown"
                    className="absolute top-full left-1/2 -translate-x-1/2 pt-3"
                  >
                    <div className="bg-white border border-[#E9ECEF] shadow-2xl rounded-lg py-3 min-w-[280px]">
                      {SPECIALTIES.map((s) => (
                        <a
                          key={s.slug}
                          href={specialtyHref(s.slug)}
                          data-testid={`specialty-link-${s.slug}`}
                          className="block px-5 py-2.5 text-sm text-[#0A2540]/85 hover:bg-[#F8F9FA] hover:text-[#0D3B66] border-l-2 border-transparent hover:border-[#D4AF37] transition-all"
                        >
                          {s.label}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <a
                key={l.id}
                href={l.href}
                data-testid={`nav-${l.id}`}
                className="text-sm text-[#0A2540]/85 hover:text-[#D4AF37] transition-colors"
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        {/* Right CTA */}
        <a
          href={whatsappLink()}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-whatsapp-cta"
          className="hidden md:inline-flex items-center bg-[#0D3B66] hover:bg-[#0A2540] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-colors flex-shrink-0"
        >
          Agendar Consulta
        </a>

        {/* Mobile toggle */}
        <button
          aria-label="Abrir menu"
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-[#0A2540]"
          onClick={() => setOpen(!open)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-[#E9ECEF]" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {links.map((l) =>
              l.dropdown ? (
                <div key={l.id} className="border-b border-[#E9ECEF]/70 last:border-0">
                  <button
                    className="w-full flex items-center justify-between py-3 text-[#0A2540] font-medium"
                    onClick={() => setMobileSpecOpen(!mobileSpecOpen)}
                    data-testid={`mobile-nav-${l.id}`}
                  >
                    {l.label}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${mobileSpecOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {mobileSpecOpen && (
                    <div className="pb-3 pl-3 flex flex-col gap-1">
                      {SPECIALTIES.map((s) => (
                        <a
                          key={s.slug}
                          href={specialtyHref(s.slug)}
                          className="py-2 text-sm text-[#0A2540]/80 border-l-2 border-[#D4AF37]/40 pl-3"
                          onClick={() => setOpen(false)}
                        >
                          {s.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={l.id}
                  href={l.href}
                  className="text-[#0A2540] py-3 border-b border-[#E9ECEF]/70 last:border-0"
                  onClick={() => setOpen(false)}
                >
                  {l.label}
                </a>
              )
            )}
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center justify-center bg-[#0D3B66] text-white font-semibold px-5 py-3 rounded-sm"
            >
              Agendar Consulta
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
;
